import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { FirebaseError } from 'firebase/app';
import { useAuthActions, useAuthState } from '../hooks/useAuth';
import { Button } from '../components/ui/Button';
import { Input, Label } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';

type Mode = 'signin' | 'signup';

/**
 * Auth errors that indicate the backend's `enforceInvitation` Function has
 * deleted the Auth user for being uninvited. The user re-hits the login
 * screen and Firebase Auth can no longer find their record; we surface the
 * "not invited" message instead of the raw technical error.
 */
const UNINVITED_AUTH_CODES = new Set<string>([
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/user-deleted',
]);

const UNINVITED_MESSAGE =
  'このメールアドレスは招待されていません。管理者にご連絡ください。';

export function translateAuthError(err: unknown): string {
  if (err instanceof FirebaseError) {
    if (UNINVITED_AUTH_CODES.has(err.code)) return UNINVITED_MESSAGE;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

export function LoginPage() {
  const { user, loading } = useAuthState();
  const { google, email, register } = useAuthActions();
  const [mode, setMode] = useState<Mode>('signin');
  const [mail, setMail] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (user) return <Navigate to="/" replace />;

  const submitEmail = async (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      if (mode === 'signin') await email(mail, pw);
      else await register(mail, pw);
    } catch (e) {
      setErr(translateAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitGoogle = async () => {
    setErr(null);
    setBusy(true);
    try {
      await google();
    } catch (e) {
      setErr(translateAuthError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-full items-center justify-center px-4">
      <div className="card w-full max-w-md p-6">
        <div className="flex items-center gap-3">
          <img
            src="/migratus_icon.png"
            alt=""
            aria-hidden
            className="h-10 w-10 rounded-lg"
          />
          <div>
            <h1 className="text-xl font-semibold tracking-wider">MIGRATUS</h1>
            <p className="text-xs text-slate-500">移行プロジェクトの管制塔</p>
          </div>
        </div>
        <p className="mt-3 text-sm text-slate-600">
          {mode === 'signin' ? 'サインインしてください' : '新規登録'}
        </p>
        <p className="mt-3 rounded bg-slate-50 px-3 py-2 text-xs text-slate-600">
          このサービスは招待制です。招待された Email
          アドレスでログインしてください。
        </p>

        <form className="mt-6 space-y-3" onSubmit={submitEmail}>
          <div>
            <Label htmlFor="email">メールアドレス</Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={mail}
              onChange={(e) => setMail(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="password">パスワード</Label>
            <Input
              id="password"
              type="password"
              required
              minLength={6}
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
            />
          </div>
          {err ? <p className="text-xs text-red-600">{err}</p> : null}
          <Button type="submit" disabled={busy} className="w-full">
            {mode === 'signin' ? 'サインイン' : 'アカウント作成'}
          </Button>
        </form>

        <div className="mt-4 flex items-center gap-2">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs text-slate-500">or</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <Button
          variant="secondary"
          className="mt-4 w-full"
          onClick={submitGoogle}
          disabled={busy}
        >
          Google でサインイン
        </Button>

        <button
          type="button"
          className="mt-4 w-full text-xs text-slate-600 hover:underline"
          onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')}
        >
          {mode === 'signin'
            ? 'アカウントをお持ちでない場合は新規登録'
            : 'すでにアカウントをお持ちの場合はサインイン'}
        </button>
      </div>
    </div>
  );
}
